<template>
  <div class="animated-fade-in">
    <v-container class="container-fluid">
      <v-card class="patient-main-card premium-card">
        <div class="card-header header-normal">
          <div class="header-content">
            <div class="patient-main-info">
              <div class="avatar-section">
                <div class="premium-avatar avatar-normal">
                  <span class="material-icons avatar-icon">
                    settings
                  </span>
                </div>
              </div>
              <div class="patient-core-info">
                <div class="name-section">
                  <h1 class="patient-name">
                    Configurações
                  </h1>
                </div>
              </div>
            </div>
          </div>
        </div>

        <v-card-text class="premium-card-content">
          <template v-if="!addNew">
            <div class="exams-section">
              <div class="section-header">
                <h2 class="section-title">
                  <span class="material-icons title-icon">save</span>
                  Configurações Salvas
                </h2>
                <v-btn 
                  variant="text"
                  class="toggle-exams-btn"
                  @click="listarCredenciaisAtendimento" 
                >
                  <span class="material-icons">refresh</span>
                  Atualizar
                </v-btn>
              </div>

              <v-alert 
                v-if="listaDeCredenciais.length === 0" 
                type="info" 
                variant="tonal" 
                class="ma-3 rounded-lg empty-state"
                border="start" 
                color="primary"
              >
                <template #prepend>
                  <v-icon
                    color="primary"
                    class="empty-icon"
                  >
                    info
                  </v-icon>
                </template>
                <div class="empty-text">
                  Nenhuma configuração salva
                </div>
              </v-alert>

              <div
                v-else
                class="configs-grid"
              >
                <div 
                  v-for="(item, index) in listaDeCredenciais" 
                  :key="index"
                  class="additional-exam-card config-card" 
                  :class="{ 'selected-config': selectedCard === item._id }"
                  @click="selecionarCredencial(item)"
                >
                  <div class="config-card-content">
                    <div class="config-card-header">
                      <div class="exam-icon">
                        <span class="material-icons">apartment</span>
                      </div>
                      <div class="exam-title">
                        {{ item.unidade?.nomeUnidade || 'Unidade não definida' }}
                      </div>
                      <div class="selection-indicator">
                        <span class="material-icons">
                          {{ selectedCard === item._id ? 'check_circle' : 'radio_button_unchecked' }}
                        </span>
                      </div>
                    </div>
                    <v-divider class="my-2" />
                    <div class="config-card-details">
                      <div class="detail-item">
                        <span class="material-icons small-icon">place</span>
                        <span class="detail-label">Local:</span>
                        <span class="detail-value">{{ item.local?.nomeLocal || 'Não definido' }}</span>
                      </div>
                      <div
                        v-if="item.localConsultorio"
                        class="detail-item"
                      >
                        <span class="material-icons small-icon">meeting_room</span>
                        <span class="detail-label">Consultório:</span>
                        <span class="detail-value">{{ item.consultorio?.valor || '' }}{{ item.localConsultorio }}</span>
                      </div>
                      <div class="detail-item">
                        <span class="material-icons small-icon">healing</span>
                        <span class="detail-label">Serviços:</span>
                        <v-chip 
                          v-if="item.servicos && item.servicos.length" 
                          size="x-small" 
                          class="services-chip ml-1"
                        >
                          {{ item.servicos.length }}
                        </v-chip>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>
                    
          <!-- Formulário Nova Configuração -->
          <v-slide-y-transition>
            <v-form
              v-show="addNew"
              ref="form"
              v-model="valid"
              lazy-validation
              class="mt-2"
            >
              <div class="exams-section">
                <div class="section-header">
                  <h2 class="section-title">
                    <span class="material-icons title-icon">add_circle</span>
                    {{ selectedCard ? 'Editar Configuração' : 'Nova Configuração' }}
                  </h2>
                </div>

                <v-row
                  dense
                  class="form-fields"
                >
                  <v-col
                    cols="12"
                    md="6"
                  >
                    <v-autocomplete 
                      v-model="unidadeSelecionada" 
                      variant="outlined" 
                      label="Unidade"
                      :items="listaUnidades" 
                      return-object
                      :rules="[v => !!v || 'Escolha a unidade de atendimento']" 
                      item-title="nomeUnidade"
                      no-data-text="Nenhuma Unidade disponível" 
                      required
                      prepend-inner-icon="local_hospital"
                      color="primary"
                      density="comfortable"
                      hide-details="auto"
                      class="premium-field"
                    />
                  </v-col>

                  <v-col
                    cols="12"
                    md="6"
                  >
                    <v-autocomplete 
                      v-model="localEscolhido" 
                      variant="outlined" 
                      label="Local de Atendimento"
                      :items="listaLocais" 
                      return-object 
                      item-value="idLocal"
                      :rules="[v => !!v || 'Local obrigatório']" 
                      item-title="nomeLocal"
                      no-data-text="Nenhum Local disponível" 
                      required 
                      prepend-inner-icon="place"
                      :disabled="!unidadeSelecionada"
                      color="primary"
                      density="comfortable"
                      hide-details="auto"
                      class="premium-field"
                    />
                  </v-col>

                  <v-col cols="12">
                    <v-autocomplete 
                      v-model="listaServicosSelecionados" 
                      variant="outlined" 
                      label="Serviços"
                      :items="listaServicosDoMedico" 
                      item-value="idServico" 
                      return-object
                      :rules="[v => v && v.length > 0 || 'Escolha pelo menos um serviço']"
                      item-title="nomeEspecialidade" 
                      no-data-text="Nenhum Serviço disponível" 
                      required
                      multiple 
                      chips 
                      closable-chips 
                      prepend-inner-icon="healing" 
                      :disabled="!localEscolhido"
                      color="primary"
                      density="comfortable"
                      hide-details="auto"
                      class="premium-field services-field"
                    >
                      <template #selection="{ item, index }">
                        <v-chip
                          v-if="index < 2"
                          size="small"
                          class="service-chip me-1"
                        >
                          <span class="chip-text">{{ item.title }}</span>
                        </v-chip>
                        <span
                          v-if="index === 2"
                          class="chips-counter ms-1"
                        >
                          +{{ listaServicosSelecionados.length - 2 }} outros
                        </span>
                      </template>
                    </v-autocomplete>
                  </v-col>
                </v-row>

                <v-row
                  dense
                  align="center"
                  class="mt-3 location-fields"
                >
                  <v-col
                    cols="12"
                    sm="4"
                  >
                    <v-select 
                      v-model="descricaoLocal" 
                      variant="outlined" 
                      :items="itensDescricaoLocal"
                      item-title="descricao" 
                      item-value="descricao" 
                      label="Tipo do Local" 
                      return-object
                      prepend-inner-icon="door_sliding" 
                      color="primary"
                      density="comfortable"
                      hide-details="auto"
                      class="premium-field"
                      @update:modelValue="validateField"
                    />
                  </v-col>
                  <v-col
                    cols="12"
                    sm="8"
                  >
                    <v-text-field 
                      v-model="nomeConsultorio" 
                      variant="outlined" 
                      :rules="rulesLocal"
                      :label="descricaoLocal?.descricao === 'Outros ' ? 'Descrição do Local' : 'Número/Identificação'"
                      prepend-inner-icon="badge"
                      color="primary"
                      density="comfortable"
                      hide-details="auto"
                      class="premium-field"
                    />
                  </v-col>
                </v-row>
              </div>
            </v-form>
          </v-slide-y-transition>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <div
            v-if="!addNew"
            class="actions-grid"
          >
            <v-btn 
              color="error" 
              variant="outlined" 
              :disabled="!selectedCard" 
              class="action-btn"
              @click="confirmDelete"
            >
              <span class="material-icons btn-icon">{{ deleteConfirmed ? 'warning' : 'delete' }}</span>
              {{ deleteConfirmed ? "Confirmar" : "Excluir" }}
            </v-btn>

            <v-btn 
              color="primary" 
              variant="outlined" 
              class="action-btn" 
              @click="addNew = true"
            >
              <span class="material-icons btn-icon">add</span>
              Nova
            </v-btn>

            <v-btn 
              color="secondary" 
              variant="outlined" 
              :disabled="!selectedCard" 
              class="action-btn"
              @click="editSelection"
            >
              <span class="material-icons btn-icon">edit</span>
              Editar
            </v-btn>

            <v-btn 
              color="success" 
              variant="flat"
              :disabled="!selectedCard" 
              class="action-btn"
              @click="confirmSelection"
            >
              <span class="material-icons btn-icon">play_arrow</span>
              Iniciar Atendimento
            </v-btn>
          </div>
          <div
            v-else
            class="actions-grid"
          >
            <v-btn 
              variant="outlined"
              class="action-btn"
              @click="addNew = false; resetForm()"
            >
              <span class="material-icons btn-icon">arrow_back</span>
              Voltar
            </v-btn>

            <v-btn 
              variant="outlined" 
              :loading="load" 
              color="primary" 
              :disabled="!valid"
              class="action-btn" 
              @click="cadastrarCredenciaisAtendimento"
            >
              <span class="material-icons btn-icon">save</span>
              Salvar
            </v-btn>

            <v-btn 
              variant="flat" 
              :loading="load" 
              color="success" 
              :disabled="!valid"
              class="action-btn" 
              @click="adicionarServicosParaAtendimento"
            >
              Iniciar Agora
              <span class="material-icons btn-icon">play_arrow</span>
            </v-btn>
          </div>
        </v-card-actions>
      </v-card>
    </v-container>
  </div>
</template>

<script>
import medicoService from "../../services/medico.service";
import {
    getToken,
    setToken
} from "../../store/auth";

export default {
    name: 'ConfiguracaoInicialPremium',
    props: {
        desabilitarAutoSelecao: {
            type: Boolean,
            default: false
        }
    },
    emits: [
        'configuracao-completa',
        'servicos-selecionados',
        'local-selecionado',
        'unidade-selecionada',
        'nome-consultorio-definido'
    ],
    data() {
        return {
            selectedCard: '',
            deleteConfirmed: false,
            addNew: false,
            load: false,
            user: {},
            valid: true,
            listaDeCredenciais: [],
            listaUnidades: [],
            unidadeSelecionada: null,
            listaLocais: [],
            localEscolhido: null,
            estruturaCompleta: [],
            listaServicosDoMedico: [],
            listaServicos: [],
            listaServicosSelecionados: [],
            descricaoLocal: { descricao: "Consultório ", valor: "Consultório " },
            nomeConsultorio: "",
            itensDescricaoLocal: [
                { descricao: "Laboratório ", valor: "Laboratório " },
                { descricao: "Consultório ", valor: "Consultório " },
                { descricao: "Sala ", valor: "Sala " },
                { descricao: "Outros ", valor: "" }
            ],
            rulesLocal: [
                v => (v && v.length <= 5) || "Mínimo 1 e máximo 5 caracteres",
                v => v[0] != " " || "Não pode iniciar com espaço em branco!!"
            ]
        };
    },
    watch: {
        unidadeSelecionada: function (val) {
            if (val && val.idUnidade) {
                this.retornarLocais(val.idUnidade)
            }
            const userUpdate = { ...this.user }
            if (val) {
                userUpdate.idUnidade = val.idUnidade
                userUpdate.nomeUnidade = val.nomeUnidade || ''
            } else {
                userUpdate.idUnidade = null
                userUpdate.nomeUnidade = ''
            }
            this.user = userUpdate
            this.$store.commit('SET_USER', userUpdate)
        },
        localEscolhido: function (val) {
            if (val && val.servicos) {
                this.listaServicosDoMedico = val.servicos
            } else {
                this.listaServicosDoMedico = [];
            }

            const userUpdate = { ...this.user }
            if (val) {
                userUpdate.idLocal = val.idLocal
                userUpdate.nomeLocal = val.nomeLocal
                userUpdate.idUnidade = this.unidadeSelecionada?.idUnidade || null
            } else {
                userUpdate.idLocal = null
                userUpdate.nomeLocal = null
                userUpdate.idUnidade = null
            }

            this.user = userUpdate
            this.$store.commit('SET_USER', userUpdate)
        }
    },
    mounted() {
        this.user = this.$store.getters.usuario;
        this.listarCredenciaisAtendimento();
        this.retornarServicosDoMedico()
    },
    methods: {
        retornarServicosDoMedico() {
            medicoService.medico
                .retornarServicosDoMedico()
                .then(rsp => {
                    console.log("Serviços do médico:", rsp.data);

                    if (rsp.data && Array.isArray(rsp.data)) {
                        this.listaUnidades = rsp.data.map(unidade => ({
                            idUnidade: unidade.idUnidade,
                            nomeUnidade: unidade.nomeUnidade
                        }));

                        this.estruturaCompleta = rsp.data;
                    } else {
                        console.warn('Dados retornados em formato inesperado:', rsp.data);
                        this.listaUnidades = [];
                        this.estruturaCompleta = [];
                    }
                })
                .catch(err => {
                    console.log("Erro ao buscar serviços:", err);
                    this.listaUnidades = [];
                    this.estruturaCompleta = [];
                });
        },

        retornarLocais(idUnidade) {
            const unidade = this.estruturaCompleta.find(u => u.idUnidade === idUnidade);

            if (unidade && unidade.locais) {
                this.listaLocais = unidade.locais.map(local => ({
                    idLocal: local.idLocal,
                    nomeLocal: local.nomeLocal,
                    servicos: local.servicos || []
                }));
            } else {
                this.listaLocais = [];
            }

            this.localEscolhido = null;
            this.listaServicosDoMedico = [];
        },

        nomeFormatado(str) {
            return str;
        },

        resetForm() {
            this.selectedCard = '';
            this.unidadeSelecionada = null;
            this.localEscolhido = null;
            this.listaServicosSelecionados = [];
            this.descricaoLocal = { descricao: "Consultório ", valor: "Consultório " };
            this.nomeConsultorio = "";

            if (this.$refs.form) {
                this.$refs.form.resetValidation();
            }
        },

        confirmDelete() {
            if (!this.deleteConfirmed) {
                this.deleteConfirmed = true;
                setTimeout(() => {
                    this.deleteConfirmed = false;
                }, 5000);
            } else {
                if (this.selectedCard) {
                    medicoService.medico.removerCredenciaisAtendimento(this.selectedCard).then(r => {
                        if (r.status == 200) {
                            this.selectedCard = '';
                            this.listarCredenciaisAtendimento();
                            this.deleteConfirmed = false;
                        }
                    }).catch(error => {
                        console.error('Erro ao excluir credencial:', error);
                        this.deleteConfirmed = false;
                    });
                }
            }
        },

        selecionarCredencial(item) {
            if (item && item._id) {
                this.selectedCard = item._id;
            }
        },

        editSelection() {
            if (!this.selectedCard) return;

            const credencial = this.listaDeCredenciais.find(item => item && item._id === this.selectedCard);
            if (credencial) {
                this.unidadeSelecionada = credencial.unidade || null;
                this.localEscolhido = credencial.local || null;
                this.listaServicosSelecionados = credencial.servicos || [];
                this.descricaoLocal = credencial.consultorio || { descricao: "Consultório ", valor: "Consultório " };
                this.nomeConsultorio = credencial.localConsultorio || "";
                this.addNew = true;
            }
        },

        confirmSelection() {
            if (!this.selectedCard) return;

            const credencial = this.listaDeCredenciais.find(item => item && item._id === this.selectedCard);
            if (credencial) {
                this.listaServicosSelecionados = credencial.servicos
                this.unidadeSelecionada = credencial.unidade
                this.localEscolhido = credencial.local
                this.descricaoLocal = credencial.consultorio || { descricao: "Consultório ", valor: "Consultório " };
                this.nomeConsultorio = credencial.localConsultorio || "";
                this.adicionarServicosParaAtendimento()
            }
        },

        listarCredenciaisAtendimento() {
            this.listaDeCredenciais = [];
            medicoService.medico.listarCredenciaisAtendimento(this.user.id).then(r => {
                if (r.status == 200) {
                    this.listaDeCredenciais = r.data || [];
                    this.selecionarUnicaConfiguracao();
                }
            }).catch(error => {
                console.error('Erro ao listar credenciais:', error);
                this.listaDeCredenciais = [];
                this.selectedCard = '';
            });
        },

        selecionarUnicaConfiguracao() {
            if (this.listaDeCredenciais.every(item => item?._id !== this.selectedCard)) {
                this.selectedCard = '';
            }
        },

        validateField() {
            this.rulesLocal = [];
            const descricao = this.descricaoLocal?.descricao || "Consultório ";

            if (descricao === "Consultório ") {
                this.rulesLocal.push(
                    v => v && v[0] != " " || "Não pode iniciar com espaço em branco!!",
                    v => (v && v.length <= 5 && v.length > 0) || "Mínimo 1 e máximo 5 caracteres"
                );
            } else if (descricao === "Outros ") {
                this.rulesLocal.push(
                    v => v && v[0] != " " || "Não pode iniciar com espaço em branco!!",
                    v => (v && v.length <= 20 && v.length > 0) || "Mínimo 1 e máximo 20 caracteres"
                );
            } else if (descricao === "Laboratório ") {
                this.rulesLocal.push(
                    v => v && v[0] != " " || "Não pode iniciar com espaço em branco!!",
                    v => (v && v.length <= 5) || "Máximo 5 caracteres"
                );
            } else if (descricao === "Sala ") {
                this.rulesLocal.push(
                    v => v && v[0] != " " || "Não pode iniciar com espaço em branco!!",
                    v => (v && v.length <= 5 && v.length > 0) || "Mínimo 1 e máximo 5 caracteres"
                );
            }
        },

        async cadastrarCredenciaisAtendimento() {
            try {
                const isValid = await this.$refs.form.validate();

                if (isValid.valid) {
                    this.load = true;

                    const credenciais = {
                        id: this.selectedCard ? this.selectedCard : '',
                        idUsuario: this.user.id,
                        unidade: {
                            nomeUnidade: this.unidadeSelecionada?.nomeUnidade || '',
                            idUnidade: this.unidadeSelecionada?.idUnidade || 0
                        },
                        local: {
                            idLocal: this.localEscolhido?.idLocal?.toString() || '',
                            nomeLocal: this.localEscolhido?.nomeLocal || '',
                            idUnidade: this.unidadeSelecionada?.idUnidade || 0
                        },
                        servicos: this.listaServicosSelecionados.map(servico => ({
                            idServico: servico.idServico || 0,
                            nomeEspecialidade: servico.nomeEspecialidade || '',
                            exames: Array.isArray(servico.exames)
                                ? servico.exames.map(exame => exame?.toString() || '')
                                : []
                        })),
                        consultorio: {
                            descricao: this.descricaoLocal?.descricao || '',
                            valor: this.descricaoLocal?.valor || ''
                        },
                        localConsultorio: this.nomeConsultorio || ''
                    };

                    console.log('Credenciais formatadas:', credenciais);

                    const response = await medicoService.medico.cadastrarCredenciaisAtendimento(credenciais)

                    this.load = false;
                    console.log('Resposta do servidor:', response.data);

                    if (response.status == 200 && response.data.status === 'success') {
                        this.addNew = false;
                        this.listarCredenciaisAtendimento();
                        this.resetForm();
                    }
                }
            } catch (error) {
                console.error('Erro ao cadastrar credenciais:', error);
                this.load = false;
                this.$toast.error('Erro ao salvar configuração');
            }
        },

        async adicionarServicosParaAtendimento() {
            let valid = true
            if (this.addNew && this.$refs.form) {
                const { valid: isValid } = await this.$refs.form.validate()
                valid = isValid
            }

            if (valid) {
                console.log('passou: :', this.listaServicosSelecionados);
                this.$store.dispatch(
                    "ServicosEscolhidos",
                    JSON.parse(
                        JSON.stringify(this.listaServicosSelecionados)
                    ))

                const userUpdate = { ...this.user }

                if (this.localEscolhido) {
                    userUpdate.idLocal = this.localEscolhido.idLocal
                    userUpdate.nomeLocal = this.localEscolhido.nomeLocal
                }

                if (this.unidadeSelecionada) {
                    userUpdate.idUnidade = this.unidadeSelecionada.idUnidade
                    userUpdate.nomeUnidade = this.unidadeSelecionada.nomeUnidade || ''
                }

                userUpdate.nomeConsultorio = (this.descricaoLocal?.valor || '') + (this.nomeConsultorio || '')
                
                this.user = userUpdate
                this.$store.commit('SET_USER', userUpdate)

                this.refreshToken()
                this.$emit('configurar', false);
            }
        },

        refreshToken() {
            console.log("refresh token chamado...", getToken())

            medicoService.medico
                .refreshToken(
                    getToken(),
                    this.localEscolhido.idLocal,
                    this.unidadeSelecionada.idUnidade
                )
                .then(rsp => {
                    console.log(rsp);
                    if (rsp.status == 200) {
                        setToken(rsp.data.token)
                    }
                })
                .catch(err => {
                    console.log(err);
                });
        },

        groupBy(collection, property) {
            const values = [];
            const result = [];

            for (let i = 0; i < collection.length; i++) {
                const val = collection[i][property];
                const index = values.indexOf(val);

                if (index > -1) {
                    result[index].push(collection[i]);
                } else {
                    values.push(val);
                    result.push([collection[i]]);
                }
            }

            return result;
        }
    }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/icon?family=Material+Icons');

.animated-fade-in {
  animation: fadeIn 0.6s ease-out;
}

@keyframes fadeIn {
  from { 
    opacity: 0; 
    transform: translateY(20px) scale(0.98); 
  }
  to { 
    opacity: 1; 
    transform: translateY(0) scale(1); 
  }
}

.container-fluid {
  padding: 16px; /* Mantém o espaçamento interno */
  /* max-width: 1200px; */ /* Removido para permitir que o contêiner ocupe toda a largura */
}

/* Card Principal */
.patient-main-card.premium-card {
  border-radius: 20px;
  overflow: hidden;
  background: rgb(var(--v-theme-surface));
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.patient-main-card:hover {
  transform: translateY(-1px);
}

/* Header */
.card-header {
  padding: 24px;
  background: rgb(var(--v-theme-surface)) !important;
  color: rgb(var(--v-theme-on-surface));
  position: relative;
  overflow: hidden;
  border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.12);
}

.header-normal {
  border-top: 4px solid rgb(var(--v-theme-primary));
}

.header-content {
  position: relative;
  z-index: 2;
}

.patient-main-info {
  display: flex;
  align-items: center;
  gap: 20px;
}

.premium-avatar {
  position: relative;
  width: 70px;
  height: 70px;
  border-radius: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
}

.avatar-normal { 
  background: linear-gradient(135deg, rgb(var(--v-theme-primary)) 0%, rgb(var(--v-theme-secondary)) 100%); 
  color: rgb(var(--v-theme-on-primary));
}

.avatar-icon {
  font-size: 32px;
}

.patient-core-info {
  flex: 1;
}

.name-section {
  margin-bottom: 0;
}

.patient-name {
  font-size: 1.75rem;
  font-weight: 800;
  color: rgb(var(--v-theme-on-surface));
}

/* Conteúdo do Card */
.premium-card-content {
  padding: 24px;
}

/* Seção de Configurações */
.exams-section {
  margin-bottom: 20px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 10px;
  color: rgb(var(--v-theme-on-surface));
  margin: 0;
}

.title-icon {
  color: rgb(var(--v-theme-primary));
  font-size: 24px;
}

.toggle-exams-btn {
  color: rgb(var(--v-theme-primary));
  font-weight: 600;
  font-size: 0.875rem;
}

/* Grid de Configurações */
.configs-grid {
  display: flex;
  overflow-x: auto;
  padding-bottom: 8px;
  gap: 16px;
}

.config-card {
  cursor: pointer;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  border-radius: 12px;
  transition: all 0.2s ease;
  padding: 16px;
  min-width: 300px;
  flex: 0 0 auto;
}

.config-card:hover {
  border-color: rgb(var(--v-theme-primary));
  transform: translateY(-2px);
}

.selected-config {
  border-color: rgb(var(--v-theme-primary)) !important;
  background: rgb(var(--v-theme-surface));
}

.config-card-content {
  display: flex;
  flex-direction: column;
}

.config-card-header {
    display: flex;
    align-items: center;
    gap: 12px;
}

.exam-icon {
  color: rgb(var(--v-theme-primary));
}

.exam-title {
  font-weight: 700;
  color: rgb(var(--v-theme-on-surface));
  font-size: 1.1rem;
  flex: 1;
}

.config-card-details {
    padding-top: 12px;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.detail-item {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 0.9rem;
}

.small-icon {
    font-size: 18px;
    color: rgb(var(--v-theme-on-surface));
}

.detail-label {
    font-weight: 600;
    color: rgb(var(--v-theme-on-surface));
}

.detail-value {
    color: rgb(var(--v-theme-on-surface));
}

.services-chip {
    background-color: rgba(var(--v-theme-primary), 0.1) !important;
    color: rgb(var(--v-theme-primary)) !important;
    font-weight: 600;
}

.selection-indicator {
    color: rgb(var(--v-theme-on-surface));
    transition: color 0.3s ease;
}

.selected-config .selection-indicator {
    color: rgb(var(--v-theme-primary));
}

/* Ações */
.premium-actions {
  padding: 20px 24px;
  background: rgb(var(--v-theme-surface-variant));
  border-top: 1px solid rgba(var(--v-theme-on-surface), 0.12);
}

.actions-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 10px;
  width: 100%;
}

.action-btn {
  border-radius: 10px;
  font-weight: 600;
  text-transform: none;
  height: 48px;
  padding: 0 16px;
  transition: all 0.3s ease;
  font-size: 0.875rem;
}

.action-btn:hover {
  transform: translateY(-1px);
}

.btn-icon {
  margin-right: 6px;
  font-size: 18px;
}

/* Formulário */
.premium-field :deep(.v-input__control) {
    border-radius: 12px;
}

.premium-field :deep(.v-field) {
    border-radius: 12px;
    transition: all 0.3s ease;
}

.premium-field :deep(.v-field--focused) {
    border-color: rgb(var(--v-theme-primary));
    box-shadow: 0 0 0 2px rgba(var(--v-theme-primary), 0.2);
}

.service-chip {
    background: linear-gradient(135deg, rgb(var(--v-theme-primary)) 0%, rgb(var(--v-theme-secondary)) 100%) !important;
    color: white !important;
}

.chip-text {
    font-size: 0.75rem;
    font-weight: 500;
}

.chips-counter {
    font-size: 0.75rem;
    color: rgb(var(--v-theme-on-surface));
    font-weight: 500;
}

.form-divider {
    border-color: rgba(var(--v-theme-primary), 0.1);
}


/* Responsividade */
@media (max-width: 768px) {
  .container-fluid {
    padding: 12px;
  }
  
  .patient-main-info {
    flex-direction: column;
    text-align: center;
    gap: 16px;
  }

  .actions-grid {
    grid-template-columns: 1fr;
  }
  
  .section-header {
    flex-direction: column;
    gap: 8px;
    align-items: flex-start;
  }
}

.material-icons {
  font-family: 'Material Icons';
  font-weight: normal;
  font-style: normal;
  font-size: 24px;
  line-height: 1;
  letter-spacing: normal;
  text-transform: none;
  display: inline-block;
  white-space: nowrap;
  word-wrap: normal;
  direction: ltr;
  -webkit-font-feature-settings: 'liga';
  -webkit-font-smoothing: antialiased;
}
</style>
